import { motion } from 'framer-motion';
import { 
  FaLock, 
  FaFileAlt, 
  FaCreditCard, 
  FaSearch, 
  FaShieldAlt, 
  FaCheckCircle,
  FaServer,
  FaUserLock,
  FaDatabase,
  FaCloud,
  FaArrowRight,
  FaShieldVirus,
  FaClock,
  FaChartLine
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const securityPillars = [
  {
    icon: FaLock,
    title: 'Data Encryption',
    description: 'All data is encrypted at rest and in transit using industry-standard AES-256 protocols.',
    color: 'from-blue-500 to-indigo-600',
    details: ['AES-256 Encryption', 'TLS 1.3', 'End-to-end security']
  },
  {
    icon: FaFileAlt,
    title: 'ISO Compliance',
    description: 'We adhere to ISO 27001 standards for information security management.',
    color: 'from-emerald-500 to-teal-600',
    details: ['ISO 27001 Certified', 'Security Policies', 'Risk Management']
  },
  {
    icon: FaCreditCard,
    title: 'PCI-DSS Compliant',
    description: 'Our payment systems meet PCI-DSS security standards for data protection.',
    color: 'from-amber-500 to-orange-600',
    details: ['PCI-DSS Certified', 'Secure Payments', 'Fraud Prevention']
  },
  {
    icon: FaSearch,
    title: 'Regular Audits',
    description: 'We conduct regular security audits and penetration testing to ensure system integrity.',
    color: 'from-purple-500 to-indigo-600',
    details: ['24/7 Monitoring', 'Penetration Testing', 'Vulnerability Scanning']
  },
];

const certifications = [
  { name: 'ISO 27001', description: 'Information Security Management', status: 'Certified' },
  { name: 'PCI DSS', description: 'Payment Card Industry Security', status: 'Compliant' },
  { name: 'GDPR', description: 'Data Protection Compliance', status: 'Compliant' },
  { name: 'SOC 2', description: 'Security & Availability', status: 'Certified' },
];

const securityFeatures = [
  { icon: FaShieldVirus, title: 'Threat Protection', description: 'Advanced threat detection and prevention systems' },
  { icon: FaUserLock, title: 'Access Control', description: 'Multi-factor authentication and role-based access' },
  { icon: FaDatabase, title: 'Data Privacy', description: 'Strict data privacy and protection policies' },
  { icon: FaCloud, title: 'Cloud Security', description: 'Secure cloud infrastructure with redundancy' },
];

const stats = [
  { number: '99.9%', label: 'Uptime', icon: FaServer },
  { number: '24/7', label: 'Monitoring', icon: FaClock },
  { number: '0', label: 'Security Breaches', icon: FaShieldAlt },
  { number: '100%', label: 'Compliance', icon: FaChartLine },
];

export default function Security() {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section className="py-16 md:py-20 bg-white min-h-screen">
      <div className="container mx-auto px-4 max-w-6xl">
        
        {/* ─── Header ─── */}
        <div className="text-center mb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-blue-100">
              Enterprise Security
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-slate-900 tracking-tight">
              Security &amp; <span className="font-semibold text-blue-600">Compliance</span>
            </h1>
            <div className="w-16 h-1 bg-blue-600 mx-auto mt-4 rounded-full" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto text-sm">
              We protect your financial data with enterprise-grade security standards and compliance certifications.
            </p>
          </motion.div>
        </div>

        {/* ─── Stats ─── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="bg-white rounded-xl p-5 text-center border border-slate-100 hover:border-blue-200 transition-all hover:shadow-md group"
              >
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-2.5 group-hover:scale-110 transition-all">
                  <Icon className="text-lg text-blue-600" />
                </div>
                <div className="text-xl font-bold text-blue-600">{stat.number}</div>
                <div className="text-xs text-slate-500 mt-0.5">{stat.label}</div>
              </motion.div>
            );
          })}
        </div>

        {/* ─── Security Pillars ─── */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-12">
          {securityPillars.map((pillar, index) => {
            const Icon = pillar.icon;
            const isHovered = hoveredIndex === index;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="group"
              >
                <div className={`bg-gradient-to-br ${pillar.color} rounded-2xl p-5 text-white h-full transition-all hover:shadow-xl hover:-translate-y-1 relative overflow-hidden`}>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                  
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3 group-hover:scale-110 transition-all">
                      <Icon className="text-2xl" />
                    </div>
                    <h3 className="font-bold text-lg mb-1.5">{pillar.title}</h3>
                    <p className="text-white/80 text-sm leading-relaxed">{pillar.description}</p>
                    <ul className="mt-3 space-y-1">
                      {pillar.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-white/70">
                          <FaCheckCircle className="text-white/50 text-[10px]" /> {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ─── Security Features ─── */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-6 bg-blue-600 rounded-full" />
            <h2 className="text-xl md:text-2xl font-bold text-slate-900">Security Features</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.08 }}
                  className="bg-white rounded-2xl p-5 border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all text-center group"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 group-hover:bg-blue-100 transition-all">
                    <Icon className="text-2xl text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 text-sm">{feature.title}</h4>
                  <p className="text-xs text-slate-500 mt-1">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ─── Detailed Sections ─── */}
        <div className="grid lg:grid-cols-2 gap-5 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <FaLock className="text-blue-600 text-lg" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Data Security &amp; Encryption</h3>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed mb-3">
              Micropoint Systems implements comprehensive security measures to protect your data. 
              All data is encrypted using AES-256 encryption standards, both at rest and in transit. 
              We use TLS 1.3 for secure communications and maintain strict access controls.
            </p>
            <ul className="space-y-1.5">
              {[
                'End-to-end encryption with AES-256',
                'Multi-factor authentication (MFA)',
                'Role-based access control (RBAC)',
                'Regular security updates and patches'
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                  <FaCheckCircle className="text-blue-500 flex-shrink-0 text-xs" /> {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <FaFileAlt className="text-emerald-600 text-lg" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Compliance Standards</h3>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed mb-3">
              We maintain compliance with international security and data protection standards, 
              ensuring your organization meets regulatory requirements.
            </p>
            <div className="space-y-1.5">
              {certifications.map((cert, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-all">
                  <div>
                    <span className="font-medium text-slate-900 text-sm">{cert.name}</span>
                    <p className="text-xs text-slate-500">{cert.description}</p>
                  </div>
                  <span className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full font-medium">
                    {cert.status}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ─── Security Audits Section ─── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all mb-12"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
              <FaShieldAlt className="text-purple-600 text-lg" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Security Audits &amp; Monitoring</h3>
          </div>
          <p className="text-slate-600 text-sm leading-relaxed mb-4">
            We conduct regular security assessments, vulnerability scans, and penetration testing 
            to identify and address potential security risks. Our systems are monitored 24/7 for 
            suspicious activities with automated alerts and incident response procedures.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {[
              { icon: FaServer, label: '24/7 Monitoring', color: 'from-blue-500 to-indigo-600' },
              { icon: FaShieldVirus, label: 'Automated Alerts', color: 'from-amber-500 to-orange-600' },
              { icon: FaSearch, label: 'Incident Response', color: 'from-emerald-500 to-teal-600' },
              { icon: FaDatabase, label: 'Recovery Planning', color: 'from-purple-500 to-indigo-600' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className={`p-3 rounded-xl bg-gradient-to-br ${item.color} text-white text-center shadow-sm`}>
                  <Icon className="text-xl mx-auto mb-0.5" />
                  <p className="text-[10px] font-medium">{item.label}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* ─── CTA Section ─── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="relative overflow-hidden rounded-2xl p-8 md:p-10 text-center text-white"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.06)_0%,_transparent_70%)]" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <div className="text-4xl mb-4">🛡️</div>
            <h2 className="text-xl md:text-2xl font-bold mb-2 text-white">
              Your Security is Our Priority
            </h2>
            <p className="text-white/50 text-sm mb-6 max-w-lg mx-auto">
              Trust us to protect your data with enterprise-grade security and compliance standards.
            </p>
            <Link to="/contact">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold text-sm transition-all inline-flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-600/30">
                Learn More About Security <FaArrowRight className="text-xs" />
              </button>
            </Link>
          </div>
        </motion.div>

      </div>
    </section>
  );
}